import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { WizardService, NavigateArgs } from 'src/app/wizard-bar/services/wizard.service';
import { of, Observable, forkJoin, Subscription, Subject } from 'rxjs';
import { UtilityAccountsGridComponent } from 'src/app/shared/components/utility-accounts-grid/utility-accounts-grid.component';
import { QuoteRequestService } from '@app/shared/services/quote-request.service';
import { QuoteRequestState } from 'src/app/shared/models/quote-request-state';
import { UtilityInformationService } from 'src/app/shared/services/utility-information.service';
import { Store } from '@ngrx/store';
import { UserRole, UtilityHubIdentity } from '@auth/models/utility-hub-identity';
import { isEqual } from 'lodash';
import { MatDialog } from '@angular/material/dialog';
import { LoadingModalComponent, LoadingModalData } from '@app/shared/components/loading-modal/loading-modal.component';
import { QuoteRequestStatus } from '@app/shared/models/quote-request-status';
import { UtilityAccount } from '@app/shared/models/UtilityAccount';
import { CustomConstants } from '@app/custom/models/custom-constants';
import { map, mergeMap } from 'rxjs/operators';
import { PricingSource, PricingSourceService } from '@app/shared/services/pricing-source.service';
import { CommodityType } from '@app/shared/enums/commodity-type';
import { AppState } from '@app/state/app.state';
import { SetCommodityType } from '@utility-information/state/actions/utility-information.actions';
import { loggedInUserSelector, loggedInUtilityHubIdentitySelector } from '@app/state/app.selectors';
import { Logger } from '@app/logging.service';
import { originalQuoteDueDateSelector, originalQuoteRequestIdSelector } from '@app/renewals/state/selectors/renewals-module.selectors';
import { SetOriginalQuoteRequestId } from '@app/renewals/state/actions/renewals-module.actions';
import { CustomService } from '@app/custom/services/custom.service';

@Component({
  selector: 'app-renewals-custom-utility-accounts',
  templateUrl: './renewals-custom-utility-accounts.component.html',
  styleUrls: ['./renewals-custom-utility-accounts.component.scss'],
})
export class RenewalsCustomUtilityAccountsComponent implements OnInit, OnDestroy {
  constructor(
    private route: ActivatedRoute,
    private wizardService: WizardService,
    public quoteService: QuoteRequestService,
    private utilityInformationService: UtilityInformationService,
    public customService: CustomService,
    private store: Store<AppState>,
    private dialog: MatDialog,
    private pricingSourceService: PricingSourceService
  ) {
    this.logger = Logger.for(this);
  }

  private initialFormValue: UtilityAccount[];
  private logger: Logger;
  quoteRequestId: any;
  grid: UtilityAccountsGridComponent;
  quoteRequestState: QuoteRequestState;
  userIdentity: UtilityHubIdentity;
  isPriceRequested: boolean;
  ownerUserId: any;
  route$: Subscription;
  dialog$: Subscription;
  selectLoggedInUser$: Subscription;
  uhIdentity$: Subscription;
  saveUtilityAccounts$: Subscription;
  nextButtonClicked: Subject<void> = new Subject<void>();
  commodity: CommodityType;
  commodityType = CommodityType;
  uom: string;
  originalQuoteRequestIdSelector$: Subscription;
  originalQuoteRequestId: number | null;
  originalQuoteRequestDueDateSelector$: Subscription;
  originalQuoteRequestDueDate: Date | null;

  ngOnInit(): void {
    this.wizardService.bindStep(this.route, (navigationArguments) => this.save(navigationArguments));

    this.originalQuoteRequestIdSelector$ = this.store.select(originalQuoteRequestIdSelector).subscribe((originalQuoteRequestId) => {
      this.originalQuoteRequestId = originalQuoteRequestId;
    });

    this.originalQuoteRequestDueDateSelector$ = this.store.select(originalQuoteDueDateSelector).subscribe((originalQuoteDueDate) => {
      this.originalQuoteRequestDueDate = originalQuoteDueDate;
    });

    this.route$ = this.route.params.subscribe((quoteRequestIdParameter) => {
      this.quoteRequestId = null;

      const urlHasQuoteRequestId =
        !isNaN(quoteRequestIdParameter.quoteRequestId) &&
        quoteRequestIdParameter.quoteRequestId !== null &&
        quoteRequestIdParameter.quoteRequestId !== undefined;

      if (urlHasQuoteRequestId) {
        this.quoteRequestId = Number(quoteRequestIdParameter.quoteRequestId);
        // Open Loading Modal
        this.dialog$ = this.dialog
          .open(LoadingModalComponent, {
            width: '25%',
            disableClose: true,
            data: {
              message: `Loading`,
              unitOfWork: this.load(),
            } as LoadingModalData,
          })
          .afterClosed()
          .subscribe((dialogReponse) => {
            // When the loading modal responds...
            if (dialogReponse) {
              // Set the dependent properties
              this.quoteRequestState = dialogReponse.state;
              this.commodity = dialogReponse.state?.commodity;
              this.uom = dialogReponse.state?.uom;
              this.isPriceRequested = dialogReponse.state?.status === QuoteRequestStatus.QuoteRequested;

              // Populate the grid with the data from the loading modal
              this.grid.populate(
                dialogReponse.utilityAccounts,
                dialogReponse.utilityInformation,
                dialogReponse.state,
                dialogReponse.quoteConfiguration,
              );

              // Set the initial form value for dirty checking
              this.initialFormValue = this.grid.getDataSnapshot();
            } else {
              // If the loading modal closes without a response, log an error
              this.logger.error('Loading Modal closed without response');
            }
          });
      }
    });

    this.selectLoggedInUser$ = this.store.select(loggedInUserSelector).subscribe((user) => {
      const userIsValid = user && !user.expired;
      if (userIsValid) {
        // Select the user identity from the store
        this.uhIdentity$ = this.store.select(loggedInUtilityHubIdentitySelector).subscribe((uhUser) => {
          this.userIdentity = uhUser;
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.route$?.unsubscribe();
    this.dialog$?.unsubscribe();
    this.selectLoggedInUser$?.unsubscribe();
    this.uhIdentity$?.unsubscribe();
    this.saveUtilityAccounts$?.unsubscribe();
    this.originalQuoteRequestIdSelector$?.unsubscribe();
    this.originalQuoteRequestDueDateSelector$?.unsubscribe();
  }

  load(): Observable<any> {
    // Wait for all services to return their observables before returning the forkJoin
    // It returns an object with the observables from each
    return forkJoin({
      state: this.quoteService.getQuoteRequestState(this.quoteRequestId),
      utilityAccounts: this.quoteService.getUtilityAccounts(this.quoteRequestId),
      quoteConfiguration: this.customService.getQuoteConfiguration(this.quoteRequestId),
    }).pipe(
      mergeMap((forkJoinResult) => {
        this.quoteRequestState = forkJoinResult.state;

        this.store.dispatch(new SetOriginalQuoteRequestId(this.quoteRequestState.renewedFromQuoteId));

        // Complete the forkJoin with the utility information observable
        return this.utilityInformationService.getUtilityInformationForQuote(forkJoinResult.state).pipe(
          map((utilityInformation) => {
            // Dispatch the commodity type to the store
            this.store.dispatch(new SetCommodityType(this.quoteRequestId, this.quoteRequestState.commodity));
            return {
              ...forkJoinResult,
              utilityInformation,
            };
          })
        );
      })
    );
  }

  gridInit(grid: UtilityAccountsGridComponent) {
    this.grid = grid;
  }

  save(navigationArguments: NavigateArgs): Observable<any> {
    const userIsSupplierManager = this.userIdentity.currentOrganisation.role == UserRole.SupplierManager;
    const canSave = this.isReadOnly() && !userIsSupplierManager;

    if (canSave) {
      return of(true);
    }

    this.grid.validateAccounts();

    return new Observable((obs) => {
      if (this.isGridDirty()) {
        if (this.grid.isValid) {
          const utilityAccounts = this.grid.getData();

          // Set the uom on the utility accounts on the grid
          utilityAccounts.forEach((x) => (x.annualUsageUOM = this.uom));

          // Save the utility accounts
          this.saveUtilityAccounts$ = this.quoteService.saveUtilityAccounts(this.quoteRequestId, utilityAccounts).subscribe(() => {
            obs.next(true);
            obs.complete();
          });
        } else {
          if (
            (navigationArguments.direction && navigationArguments.direction < 0) ||
            this.wizardService.clickedStepIsPreviousStep(CustomConstants.wizardSteps, navigationArguments.step)
          ) {
            obs.next(true);
          } else {
            obs.next(false);
          }
        }
      } else {
        obs.next(true);
      }
    });
  }

  next() {
    this.nextButtonClicked.next();
    this.wizardService.next(this.isQuoteRequestInvalidated());
  }

  back() {
    this.wizardService.previous(this.isQuoteRequestInvalidated());
  }

  isReadOnly() {
    return this.quoteService.isReadonly(this.quoteRequestId, this.quoteRequestState?.status, this.userIdentity?.id, this.quoteRequestState?.ownerUserId);
  }

  isQuoteRequestInvalidated() {
    return (
      this.hasFormChanged() && this.quoteRequestState?.status !== QuoteRequestStatus.InProgress && !this.isSupplierManagerValidChange()
    );
  }

  isGridDirty(): boolean {
    // The ticket I'm working on specifies the Matrix route only, so for now I implemented this here.
    // This could go in the utility accounts grid component when the custom route gets this logic,
    // but the definition of dirty here excludes changes from I don't know to a valid value, logic that may not be universal.
    let dirty = false;
    const gridData = this.grid.getData();

    if (gridData.length !== this.initialFormValue?.length) return true;

    if (this.hasFormChanged()) {
      dirty = true;
    }
    return dirty;
  }

  isSupplierManagerValidChange() {
    if (this.userIdentity.currentOrganisation.role === UserRole.SupplierManager && !this.isGridDirty()) {
      return true;
    }
    return false;
  }

  hasFormChanged() {
    if (!this.grid) {
      return false;
    }
    return !isEqual(this.initialFormValue, this.grid.getData());
  }

  isSupplierManagerManualEntry(): Observable<boolean> {
    return new Observable<boolean>((n) => {
      if (!this.quoteRequestState) {
        n.next(false);
      } else {
        this.pricingSourceService
          .getPricingSource(this.quoteRequestState.quoteRequestType, this.quoteRequestState.commodity)
          .subscribe((ps) => {
            n.next(ps.source === PricingSource.ManualEntry && this.userIdentity.isSupplierManager);
            n.complete();
          });
      }
    });
  }
}
